import type { PiletApi } from '@chess/platform-web';
import { CHESS_API, type Chess } from '@chess/api-chess';
import { GAMES_API, type Games } from '@chess/api-games';
import { ANALYSIS_API, type Analysis } from '@chess/api-analysis';
import { PHASES_API, endgameStartOf, openingEndOf, type PhasesProvider } from '@chess/api-review';
import { gameFacts, hasBlunder } from '@chess/api-insights';
import { gameMistakeKeys, memoisedMotifs, MOTIFS_API, type Motifs } from '@chess/api-mistakes';
import { LOG_API, type Log } from '@chess/api-log';
import { STORAGE_API, type Storage } from '@chess/api-storage';
import { MISTAKE_RULES_API, STORE_API, type MistakeRulesSource, type ReviewPhases } from '@chess/api-model';
import { GameStoreService } from './store.ts';
import { readUsername } from './owner.ts';

// Registers the game store (`STORE_API`) over the games, chess, analysis and
// phases APIs, and keeps it current: the games API's added and removed
// events, and the analysis API's completed scans. The store only reads;
// importing and scanning write through their own APIs. The facts follow the
// user's blunder and miss rules (`MISTAKE_RULES_API`, the defaults without
// it): the store waits for them before its first read, and reads every
// game's facts again when they change. The `mistake` filter's motif keys
// (`blunder-motif-pin`, `blunder-motif-pin/king`) come from the
// `mistakes.motifs` service, awaited at setup and memoised per fact
// (`memoisedMotifs`), so each mistake is classified once per game read.
// The store holds one account's games: the chess.com username in settings
// (`settings/chesscom.json`), read at setup and again on
// `settings.chesscom.changed`, when the window is reloaded for the new
// account; the old account's games stay in storage, unread.
// See docs/architecture/core/plugin-shared-store.md.
export function setup(app: PiletApi) {
  void (async () => {
    const [games, chess, analysis, motifs, log, settings] = await Promise.all([
      app.getApi<Games>(GAMES_API),
      app.getApi<Chess>(CHESS_API),
      app.getApi<Analysis>(ANALYSIS_API),
      app.getApi<Motifs>(MOTIFS_API),
      app.getApi<Log>(LOG_API).then((l) => l.child(app.meta.name)),
      app.getApi<Storage>(STORAGE_API).then((s) => s.scope('settings')),
    ]);
    let owner = readUsername(await settings.readText('chesscom.json'));
    const motifOf = memoisedMotifs(motifs);
    // Optional: without the settings plugin the defaults apply.
    const rules = await Promise.race([
      app.getApi<MistakeRulesSource>(MISTAKE_RULES_API).then(async (r) => (await r.ready(), r)),
      new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), 2000)),
    ]);

    const store = new GameStoreService({
      records: (since) => games.records({ since }),
      record: (id) => games.get(id),
      facts: async (record) => {
        const parsed = chess.parsePgn(record.pgn);
        const provider = app.tryGetApi<PhasesProvider>(PHASES_API);
        const phases: ReviewPhases = provider
          ? await provider.phasesOf({ moves: parsed.moves, positions: parsed.positions })
          : { openingEnd: openingEndOf(parsed.moves.length, undefined), endgameStart: endgameStartOf(parsed.positions) };
        const scan = await analysis.get(record.id);
        return gameFacts({ record, moves: parsed.moves, phases, scan, rules: rules?.get() }, undefined, (extractor, error) =>
          log.warn('a fact extractor failed', { id: record.id, extractor: extractor.id, error: String(error) } as never),
        );
      },
      scan: (id) => analysis.get(id),
      hasBlunder,
      mistakeKeys: (facts, now) => gameMistakeKeys(facts, now, motifOf),
      now: () => Date.now(),
      warn: (message, fields) => log.warn(message, fields as never),
      owner: () => owner,
    });

    app.registerApi(STORE_API, store);
    games.subscribe((e) => {
      if (e.type === 'added') void store.added(e.game.id);
      else store.removed(e.id);
    });
    analysis.subscribe((e) => {
      if (e.type === 'complete') void store.scanned(e.gameId);
    });
    rules?.subscribe(() => void store.refreshFacts());
    app.on('settings.chesscom.changed', ({ username }) => {
      if (username === owner) return;
      owner = username;
      void store.reload();
    });
    await store.load();
  })();
}
